<template>
  <rich-text-editor
    v-model="val"
    class="flex overflow-hidden"
    :label="label"
    :height="height"
    :hint="hint"
    dense
    bold-allowed
    italic-allowed
    :align-allowed="alignAllowed"
    :error-messages="errors"
  >
    <template #menubar="{editor}">
      <filled-button
        v-if="resettable"
        class="mr-2"
        icon="mdi-close"
        :disabled="!resetEnabled"
        :tooltip="$t('button.reset_to_default')"
        @click="emit('reset')"
      />
      <v-menu offset-y>
        <template #activator="{ on }">
          <v-btn
            class="mr-2"
            color="primary"
            outlined
            small
            v-on="on"
          >
            <v-icon x-small>
              mdi-dots-horizontal
            </v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-subheader style="height: 32px">
            {{ $t('button.insert_placeholder') }}
          </v-subheader>
          <v-list-item
            v-for="(item, i) in placeholders"
            :key="i"
            @click="editor?.commands.insertHandlebar({ id: `${item.id}`, label: item.name })"
          >
            <v-list-item-title>{{ item.name }}</v-list-item-title>
          </v-list-item>
          <v-divider v-if="$slots['menu-items']" />
          <slot name="menu-items" />
        </v-list>
      </v-menu>
    </template>
  </rich-text-editor>
</template>

<script lang="ts" setup>
import RichTextEditor from '@/components/editors/RichTextEditor.vue';
import FilledButton from '@/components/buttons/FilledButton.vue';
import MessageHandlebar from '@/model/MessageHandlebar';
import { PropType, ref, watch } from 'vue';
import { TranslateResult } from 'vue-i18n';

const props = defineProps({
  placeholders: { type: Object as PropType<Array<MessageHandlebar>>, required: true },
  height: { type: String, required: false, default: '200px' },
  label: { type: [String, Object] as PropType<string|TranslateResult>, required: false, default: '' },
  hint: { type: [String, Object] as PropType<string|TranslateResult>, required: false, default: '' },
  value: { type: String as PropType<string>, required: true },
  resettable: { type: Boolean, required: false, default: false },
  resetEnabled: { type: Boolean, required: false, default: false },
  alignAllowed: { type: Boolean, required: false, default: false },
});

const emit = defineEmits<{(e: 'input', event: string): void, (e: 'reset'): void}>();
const errors = ref([]);

const val = ref(props.value);

watch(() => props.value, () => { val.value = props.value; });

watch(val, () => emit('input', val.value));
</script>
